import React, { useMemo } from 'react'

import { TrendingUp, Calendar, Target, AlertCircle } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function SalesForecastingPage() {
  const opportunities = useCRMStore((state) => state.opportunities)
  const customers = useCRMStore((state) => state.customers)

  const forecast = useMemo(() => {
    const today = new Date()
    const thisMonth = today.getMonth()
    const thisYear = today.getFullYear()

    const months = []
    for (let i = 0; i < 6; i++) {
      const date = new Date(thisYear, thisMonth + i, 1)
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        date,
      })
    }

    const forecast = months.map(m => {
      const opps = opportunities.filter(o => {
        const closeDate = new Date(o.closeDate)
        return closeDate.getMonth() === m.date.getMonth() && closeDate.getFullYear() === m.date.getFullYear()
      })

      return {
        month: m.month,
        total: opps.reduce((sum, o) => sum + o.value, 0),
        weighted: opps.reduce((sum, o) => sum + (o.value * o.probability / 100), 0),
        count: opps.length,
        conservative: opps.reduce((sum, o) => sum + (o.value * Math.max(o.probability - 20, 0) / 100), 0),
      }
    })

    return forecast
  }, [opportunities])

  const stats = useMemo(() => {
    const totalForecast = forecast.reduce((sum, m) => sum + m.weighted, 0)
    const conservativeForecast = forecast.reduce((sum, m) => sum + m.conservative, 0)
    const totalOpportunities = forecast.reduce((sum, m) => sum + m.count, 0)

    return {
      totalForecast,
      conservativeForecast,
      totalOpportunities,
      avgDealSize: totalOpportunities > 0 ? opportunities.reduce((sum, o) => sum + o.value, 0) / opportunities.length : 0,
    }
  }, [forecast, opportunities])

  const bestCase = useMemo(() => {
    return opportunities.reduce((sum, o) => sum + o.value, 0)
  }, [opportunities])

  const worstCase = useMemo(() => {
    const activeOpps = opportunities.filter(o => o.stage !== 'Closed Lost')
    return activeOpps.reduce((sum, o) => sum + (o.value * Math.max(o.probability - 30, 0) / 100), 0)
  }, [opportunities])

  const getMonthMaxValue = useMemo(() => {
    return Math.max(...forecast.map(m => m.total), 1)
  }, [forecast])

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Sales Forecasting</h1>
            <p className="text-slate-400">Revenue predictions and scenario analysis</p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Expected Forecast</p>
                  <p className="text-3xl font-bold text-blue-400">${(stats.totalForecast / 1000).toFixed(0)}K</p>
                </div>
                <TrendingUp className="text-blue-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">Probability-weighted pipeline</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Conservative Case</p>
                  <p className="text-3xl font-bold text-yellow-400">${(stats.conservativeForecast / 1000).toFixed(0)}K</p>
                </div>
                <AlertCircle className="text-yellow-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">-20% probability adjustment</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Best Case</p>
                  <p className="text-3xl font-bold text-green-400">${(bestCase / 1000).toFixed(0)}K</p>
                </div>
                <Target className="text-green-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">All deals close at full value</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm mb-2">Avg Deal Size</p>
                  <p className="text-3xl font-bold text-purple-400">${(stats.avgDealSize / 1000).toFixed(0)}K</p>
                </div>
                <Calendar className="text-purple-500" size={24} />
              </div>
              <p className="text-xs text-slate-500">{stats.totalOpportunities} opportunities</p>
            </div>
          </div>

          {/* 6-Month Forecast */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {/* Revenue Forecast Chart */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <h3 className="text-lg font-bold text-white mb-6">6-Month Revenue Forecast</h3>

              <div className="space-y-4">
                {forecast.map((month, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-sm font-semibold text-slate-300">{month.month}</span>
                      <span className="text-sm font-bold text-blue-300">${(month.weighted / 1000).toFixed(0)}K</span>
                    </div>

                    <div className="space-y-1">
                      <div className="w-full h-6 bg-slate-700 rounded-lg overflow-hidden flex items-center">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                          style={{ width: `${(month.weighted / getMonthMaxValue) * 100}%` }}
                        ></div>
                      </div>

                      <div className="flex justify-between text-xs text-slate-400">
                        <span>{month.count} deals</span>
                        <span>Total: ${(month.total / 1000).toFixed(0)}K</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Forecast Scenarios */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <h3 className="text-lg font-bold text-white mb-6">Forecast Scenarios</h3>

              <div className="space-y-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-semibold text-white">Best Case</p>
                      <p className="text-xs text-slate-400">All deals close at full value</p>
                    </div>
                    <span className="text-2xl font-bold text-green-400">${(bestCase / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="w-full h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 w-full"></div>
                  </div>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-semibold text-white">Expected Case</p>
                      <p className="text-xs text-slate-400">Probability-weighted forecast</p>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">${(stats.totalForecast / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="w-full h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${(stats.totalForecast / bestCase) * 100}%` }}></div>
                  </div>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-semibold text-white">Conservative Case</p>
                      <p className="text-xs text-slate-400">-20% probability adjustment</p>
                    </div>
                    <span className="text-2xl font-bold text-yellow-400">${(stats.conservativeForecast / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="w-full h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500" style={{ width: `${(stats.conservativeForecast / bestCase) * 100}%` }}></div>
                  </div>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-semibold text-white">Worst Case</p>
                      <p className="text-xs text-slate-400">-30% probability adjustment</p>
                    </div>
                    <span className="text-2xl font-bold text-red-400">${(worstCase / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="w-full h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500" style={{ width: `${Math.max((worstCase / bestCase) * 100, 5)}%` }}></div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-blue-500/20">
                <p className="text-xs text-slate-400 text-center">
                  Range: ${(worstCase / 1000).toFixed(0)}K - ${(bestCase / 1000).toFixed(0)}K
                </p>
              </div>
            </div>
          </div>

          {/* Opportunity Details Table */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="p-6 border-b border-blue-500/20">
              <h3 className="text-xl font-bold text-white">Forecast Details</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20 bg-slate-900/50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Opportunity</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Close Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Probability</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Forecast</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp) => {
                    const forecast = opp.value * opp.probability / 100
                    return (
                      <tr key={opp.id} className="border-b border-blue-500/10 hover:bg-slate-800/50 transition-colors">
                        <td className="px-6 py-4 text-white font-medium">{opp.title}</td>
                        <td className="px-6 py-4 text-slate-400">{new Date(opp.closeDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                        <td className="px-6 py-4 text-green-400 font-semibold">${(opp.value / 1000).toFixed(0)}K</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${opp.probability}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-blue-300 font-semibold">{opp.probability}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-blue-400 font-semibold">${(forecast / 1000).toFixed(0)}K</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  )
}
